import { openDB, DBSchema, IDBPDatabase } from 'idb';

// Define the image object structure
export interface EstimateImage {
  id: string;
  dataUrl: string;
  fileName: string;
  description: string;
  timestamp: number;
  size: number;
  estimateId?: string; // Add estimateId to associate with an estimate
}

// Define the database schema
interface ImageDBSchema extends DBSchema {
  images: {
    key: string;
    value: EstimateImage & { estimateId: string }; // Ensure estimateId is required in DB
    indexes: {
      'by-estimate': string;
    };
  };
}

// Database configuration
const DB_CONFIG = {
  name: 'glass-estimate-images-db',
  version: 2, // Increment version to trigger upgrade
  storeName: 'images',
  estimateIdIndex: 'by-estimate'
};

// Maximum image size in bytes (5MB)
export const MAX_IMAGE_SIZE = 5 * 1024 * 1024;

// Maximum storage size warning threshold (80% of 50MB)
export const STORAGE_WARNING_THRESHOLD = 0.8 * 50 * 1024 * 1024;

let dbInstance: IDBPDatabase<ImageDBSchema> | null = null;

/**
 * Initialize the IndexedDB database for image storage
 */
const initDB = async (): Promise<IDBPDatabase<ImageDBSchema>> => {
  if (dbInstance) return dbInstance;

  try {
    dbInstance = await openDB<ImageDBSchema>(DB_CONFIG.name, DB_CONFIG.version, {
      upgrade(db, oldVersion, newVersion, transaction) {
        console.log(`Upgrading IndexedDB from version ${oldVersion} to ${newVersion}`);

        // Create the images object store if it doesn't exist
        if (!db.objectStoreNames.contains(DB_CONFIG.storeName)) {
          console.log('Creating images object store');
          const store = db.createObjectStore(DB_CONFIG.storeName, { keyPath: 'id' });
          // Create an index to quickly find images by estimate ID
          store.createIndex(DB_CONFIG.estimateIdIndex, 'estimateId');
        } else {
          // Make sure the index exists
          const store = transaction.objectStore(DB_CONFIG.storeName);
          if (!store.indexNames.contains(DB_CONFIG.estimateIdIndex)) {
            console.log('Creating by-estimate index');
            store.createIndex(DB_CONFIG.estimateIdIndex, 'estimateId');
          }
        }
      },
    });

    console.log('IndexedDB initialized successfully');
    return dbInstance;
  } catch (error) {
    console.error('Error initializing IndexedDB:', error);
    throw error;
  }
};

/**
 * Save an image to IndexedDB
 */
export const saveImage = async (
  estimateId: string,
  imageData: Omit<EstimateImage, 'id' | 'timestamp' | 'estimateId'>
): Promise<EstimateImage> => {
  const db = await initDB();

  // Create a new image object with ID, timestamp, and estimateId
  const image: EstimateImage = {
    ...imageData,
    id: `${estimateId}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
    timestamp: Date.now(),
    estimateId: estimateId // Include estimateId in the returned object
  };

  // Save the image to IndexedDB
  await db.put(DB_CONFIG.storeName, image);

  console.log(`Saved image ${image.id} for estimate ${estimateId}`);
  return image;
};

/**
 * Get all images for a specific estimate
 */
export const getImagesForEstimate = async (estimateId: string): Promise<EstimateImage[]> => {
  const db = await initDB();

  // Use the index to get all images for this estimate
  return db.getAllFromIndex(DB_CONFIG.storeName, DB_CONFIG.estimateIdIndex, estimateId);
};

/**
 * Delete an image by ID
 */
export const deleteImage = async (imageId: string): Promise<void> => {
  const db = await initDB();
  await db.delete(DB_CONFIG.storeName, imageId);
};

/**
 * Delete all images for a specific estimate
 */
export const deleteImagesForEstimate = async (estimateId: string): Promise<void> => {
  const db = await initDB();

  // Get all images for this estimate
  const images = await getImagesForEstimate(estimateId);

  // Delete each image
  const tx = db.transaction(DB_CONFIG.storeName, 'readwrite');
  await Promise.all(images.map(image => tx.store.delete(image.id)));
  await tx.done;
};

/**
 * Update an image's description
 */
export const updateImageDescription = async (
  imageId: string,
  description: string
): Promise<void> => {
  const db = await initDB();

  // Get the image
  const tx = db.transaction(DB_CONFIG.storeName, 'readwrite');
  const image = await tx.store.get(imageId);

  if (!image) {
    throw new Error(`Image with ID ${imageId} not found`);
  }

  // Update the description
  image.description = description;

  // Save the updated image
  await tx.store.put(image);
  await tx.done;
};

/**
 * Get the total storage used by all images
 */
export const getTotalStorageUsed = async (): Promise<number> => {
  const db = await initDB();

  // Get all images
  const images = await db.getAll(DB_CONFIG.storeName);

  // Calculate total size
  return images.reduce((total, image) => total + image.size, 0);
};

/**
 * Check if storage is almost full
 */
export const isStorageAlmostFull = async (): Promise<boolean> => {
  const totalUsed = await getTotalStorageUsed();
  return totalUsed > STORAGE_WARNING_THRESHOLD;
};

/**
 * Convert a File object to a data URL
 */
export const fileToDataUrl = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      resolve(reader.result as string);
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsDataURL(file);
  });
};

/**
 * Create a thumbnail from a data URL
 */
export const createThumbnail = (dataUrl: string, maxWidth = 100, maxHeight = 100): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      // Calculate new dimensions while maintaining aspect ratio
      let width = img.width;
      let height = img.height;

      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }

      if (height > maxHeight) {
        width = (width * maxHeight) / height;
        height = maxHeight;
      }

      // Create a canvas and draw the resized image
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      ctx.drawImage(img, 0, 0, width, height);

      // Convert the canvas to a data URL
      resolve(canvas.toDataURL('image/jpeg', 0.7));
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    img.src = dataUrl;
  });
};

/**
 * Compress an image data URL to reduce size
 */
export const compressImage = (dataUrl: string, maxWidth = 1200, maxHeight = 1200, quality = 0.7): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      // Calculate new dimensions while maintaining aspect ratio
      let width = img.width;
      let height = img.height;

      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }

      if (height > maxHeight) {
        width = (width * maxHeight) / height;
        height = maxHeight;
      }

      // Create a canvas and draw the resized image
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      ctx.drawImage(img, 0, 0, width, height);

      // Convert the canvas to a data URL with compression
      resolve(canvas.toDataURL('image/jpeg', quality));
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    img.src = dataUrl;
  });
};

/**
 * Calculate the size of a data URL in bytes
 */
export const getDataUrlSize = (dataUrl: string): number => {
  // Remove the data URL prefix to get the base64 string
  const base64 = dataUrl.split(',')[1];

  // Calculate the size in bytes (each base64 character represents 6 bits, so 4 characters = 3 bytes)
  return Math.ceil((base64.length * 3) / 4);
};

/**
 * Format a file size in bytes to a human-readable string
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) {
    return `${bytes} B`;
  } else if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  } else {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
};
